import {
  init as sentryInit,
  captureException as sentryCaptureException,
  addBreadcrumb as sentryAddBreadcrumb,
  setTags as sentrySetTags,
  setContext,
  httpClientIntegration,
  httpContextIntegration,
  extraErrorDataIntegration,
} from "@sentry/react";
import type {
  SeverityLevel as SentrySeverityLevel,
  ErrorEvent,
} from "@sentry/react";
import {} from "launchdarkly-react-client-sdk";

export type Primitive =
  | number
  | string
  | boolean
  | bigint
  | symbol
  | null
  | undefined;
export type SentryTag = {
  [key: string]: Primitive;
};

/**
 * ignoreErrors and denyUrls are intended to block Sentry exceptions from 3rd
 * party extensions / globals that may be contaminating our Sentry logs.
 *
 * More info: https://docs.sentry.io/clients/javascript/tips/#decluttering-sentry
 */
const ignoreErrors = [
  // Random plugins/extensions
  "top.GLOBALS",
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "http://tt.epicplay.com",
  "Can't find variable: ZiteReader",
  "jigsaw is not defined",
  "ComboSearch is not defined",
  "http://loading.retry.widdit.com/",
  "atomicFindClose",
  // Facebook borked
  "fb_xd_fragment",
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  "bmi_SafeAddOnload",
  "EBCallBackMessageReceived",
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  "conduitPage",
  // Generic error code from errors outside the security sandbox
  // You can delete this if using raven.js > 1.0, which ignores these automatically.
  "Script error.",
  // Avast extension error
  "_avast_submit",
  // Likely 3rd party making duplicate variable declaration. Also ignored in other Sentry configs: https://gist.github.com/pioug/b006c983538538066ea871d299d8e8bc
  "change_ua",

  /**
   * Ignore error being caused by Launch Darkly failed request when user is using uBlock. More info: https://github.com/launchdarkly/js-client-sdk/issues/255
   */
  "A network error occurred.",
  /**
   * Ignore Unhandled rejection error occuring specifically on Safari used on mac OS. More info: More info: https://github.com/WordPress/openverse-frontend/issues/1632
   */
  "Non-Error promise rejection captured with value: Not implemented on this platform",
  /**
   * Ignore error "TypeError annule". When the page loads, the refresh button changes to cross button,
   * now if some api request is in progress during this page loading time and the user click this cross button, then iOS chrome/safari throws this error.
   */
  "TypeError: annulé",
  "TypeError: cancelled",
  /**
   * Ignoring error caused by Safari extensions. Extension at fault seems to be 1Password. There's not enough data to share.
   */
  "Error: Error response received for message <get-frame-manager-configuration>",
  // NOTE: "TypeError: Load failed" (Safari's generic fetch-failure) used to be
  // ignored here as low-connection noise. It is intentionally NO LONGER dropped:
  // it's also how Safari surfaces requests blocked by Advanced Tracking &
  // Fingerprinting Protection / Private Browsing (LL22-11667), and dropping it
  // blinded us to that failure class. It's now kept (at error severity) and
  // grouped into one issue in beforeSend (see isResourceLoadError).
  /**
   * Ignoring error caused by CefSharp Chromium bot attempting to interact with page.
   */
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:(update|simulateEvent), ParamCount:\d+$/,

  /**
   * ---- Common, unactionable map-library noise (mapbox-gl / maplibre-gl /
   * Google Maps) ----
   *
   * These three signatures are widely recommended for filtering: they don't
   * indicate a bug we can fix. This is intentionally narrow — every OTHER map
   * error still reports (and is tagged source=map-vendor in beforeSend) so we
   * keep visibility into real issues (bad coordinates, invalid tokens, quota,
   * integration mistakes, etc.).
   */
  // Tile/source fetch aborted during pan, zoom, or unmount — internal cleanup,
  // the map keeps working. https://github.com/mapbox/mapbox-gl-js/issues/10498
  "AbortError",
  "The user aborted a request",
  "signal is aborted without reason",
  // WebGL context lost / context-count limits — driven by the device GPU,
  // browser, or too many maps on a page; not fixable in our code.
  // https://github.com/mapbox/mapbox-gl-js/issues/7332
  "Too many active WebGL contexts",
  /CONTEXT_LOST_WEBGL/i,
  /WebGL context (?:was )?lost/i,
  // ResizeObserver loop notifications — a browser safety mechanism tripped by
  // responsive containers (maps, charts). Universally benign.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];

const denyUrls = [
  // Google Adsense
  /pagead\/js/i,
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  // Seems to be google checking for internet connection
  /maps\.googleapis\.com\/maps\/api\/mapsjs\/gen_204\//i,
];

const allowUrls = [/staging.locallogic.co/, /sdk.locallogic.co/];

/**
 * Third-party map libraries whose errors we keep but flag, rather than drop, so
 * we don't go blind to real bugs that surface through them (bad coordinates,
 * invalid tokens, quota errors, etc.). Matched against the throw-site frame's
 * (minified) filename. The `*-vendor` names come from manualChunks in
 * vite.config.ts — including @vis.gl/react-google-maps, bundled into
 * google-maps-vendor.
 *
 * Note: Google's underlying maps engine still loads externally from
 * maps.googleapis.com (per Google's terms it can't be self-bundled like
 * mapbox-gl/maplibre-gl). The second google pattern catches that only if it
 * reaches beforeSend — allowUrls filters most external-frame errors first.
 */
const mapVendorPatterns: { vendor: string; pattern: RegExp }[] = [
  { vendor: "maplibre", pattern: /maplibre-vendor/ },
  { vendor: "mapbox", pattern: /mapbox-vendor/ },
  {
    vendor: "google-maps",
    pattern: /google-maps-vendor|maps\.(googleapis|gstatic)\.com/,
  },
];

/**
 * Returns which map library an exception was thrown from, or null. Sentry orders
 * frames oldest-first, so the last frame is the crash site; for chained
 * exceptions we inspect the most recent value.
 */
const getMapVendor = (event: ErrorEvent): string | null => {
  const values = event.exception?.values;
  if (!values?.length) return null;

  const frames = values[values.length - 1]?.stacktrace?.frames;
  if (!frames?.length) return null;

  const topFrame = frames[frames.length - 1];
  const file = topFrame?.filename || topFrame?.abs_path || "";

  return (
    mapVendorPatterns.find(({ pattern }) => pattern.test(file))?.vendor ?? null
  );
};

/**
 * Dynamic-import / code-split chunk load failures. These are dominated by
 * transient network + in-app-webview fetch failures of chunks that still exist
 * at origin (see LL22-11685) — not a bug we can fix in code. We keep them (a
 * sustained spike still signals a genuinely broken deploy) but collapse every
 * minified variant into ONE issue and downgrade to `warning`, so they stop
 * paging and stop fragmenting into a new Sentry group per deploy.
 */
const CHUNK_LOAD_ERROR =
  /Importing a module script failed|Unable to preload|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

const isChunkLoadError = (event: ErrorEvent): boolean => {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(
    (value) =>
      CHUNK_LOAD_ERROR.test(value?.value || "") ||
      CHUNK_LOAD_ERROR.test(value?.type || ""),
  );
};

/**
 * Generic resource/fetch load failures. On Safari these surface as
 * "TypeError: Load failed" — which is also how Safari reports a cross-origin
 * request BLOCKED by Advanced Tracking & Fingerprinting Protection / Private
 * Browsing (the LL22-11667 map-goes-gray failure). We used to drop these as
 * low-connection noise, which meant we had no visibility into how often Safari
 * privacy settings break the embedded SDK. Now we keep them at error severity
 * (a blocked resource means the SDK/map does not render — a real broken
 * experience) and only group them into one issue so they're measurable by
 * volume, browser, OS version, and any captured request URL.
 */
const RESOURCE_LOAD_ERROR =
  /\bLoad failed|NetworkError when attempting to fetch/i;

const isResourceLoadError = (event: ErrorEvent): boolean => {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.some(
    (value) =>
      RESOURCE_LOAD_ERROR.test(value?.value || "") ||
      RESOURCE_LOAD_ERROR.test(value?.type || ""),
  );
};

/**
 * Signatures of automated traffic — declared crawlers, scrapers, headless
 * browsers, and scripting HTTP clients. Matched against navigator.userAgent.
 * Kept intentionally conservative so we don't drop real users' errors.
 */
const BOT_USER_AGENT =
  /bot|crawl|spider|slurp|mediapartners|headless|phantom|puppeteer|playwright|selenium|scrapy|curl|wget|python-requests|axios|facebookexternalhit|bingpreview|lighthouse|pingdom|gtmetrix|semrush|ahrefs|mj12|dotbot|petalbot|bytespider|dataprovider/i;

/**
 * Best-effort, client-side detection of bot traffic. We use this to drop
 * failed-request errors coming from automation: crawlers and scrapers hit the
 * data APIs at volume with keys/origins that get 4xx'd, and were the bulk of
 * our Sentry noise, but they aren't a real user problem we can fix.
 *
 * Limitation: this only catches automation that *declares* itself (bot UA,
 * webdriver flag, empty UA). Scrapers that spoof a real browser UA can't be
 * reliably identified from the browser. Those are better handled server-side
 * (WAF / API rate-limiting) or via a Sentry inbound IP/geo filter.
 */
const isLikelyBot = (): boolean => {
  if (typeof navigator === "undefined") return false;
  // Automation-controlled browsers (Puppeteer, Playwright, Selenium) set this.
  if (navigator.webdriver) return true;
  const ua = navigator.userAgent || "";
  // Real browsers always send a user agent; an empty one implies a script.
  if (!ua) return true;
  return BOT_USER_AGENT.test(ua);
};

const CONSOLE_LOG_IDENTIFER = "[Local Logic] ";

/**
 * This is a light wrapper around Sentry. We can augment different Sentry
 * methods to be more useful to our needs.
 */
class ErrorTracker {
  constructor() {
    sentryInit({
      dsn: "https://19707ff4ff7f420b9369a3e15041c787@o227377.ingest.sentry.io/6677584",
      tracesSampleRate: import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE || 0.01,
      // Default values, we can adjust if this is too much/little
      // This sets the sample rate to be 10%
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.5,
      environment: import.meta.env.MODE,
      enabled:
        import.meta.env.MODE === "production" ||
        import.meta.env.VITE_SENTRY_ENABLED === "true",
      ignoreErrors,
      denyUrls,
      allowUrls,
      // Source maps now resolve via Debug IDs injected by @sentry/vite-plugin
      // at build time, so resolution no longer depends on release/dist matching.
      // release/dist are still reported for release health + event grouping and
      // should stay aligned with the "Create Sentry release" step in
      // .github/workflows/release.yml:
      //   release ← action-release `version` (the sdks-app package version)
      //   dist    ← action-release `dist`    (the commit SHA)
      release: FULL_APP_VERSION, // == npm_package_version (see vite.config.ts define)
      dist: VITE_COMMIT_SHA, // == `git rev-parse HEAD`, trimmed (see vite.config.ts)
      integrations: [
        httpClientIntegration({
          // Capture 4xx–5xx except the auth codes 401 and 403. Those are
          // expected outcomes (a key without access, or bad/spoofed origins),
          // not bugs we can fix, and were the bulk of our noise. Everything else
          // — 400, 402, and 404–599 — is kept, and bot-driven noise across those
          // codes is stripped separately in beforeSend via isLikelyBot.
          failedRequestStatusCodes: [400, 402, [404, 599]],
          // Which hosts count as "our API" for failure reporting. We enumerate
          // the actual API hosts rather than a blanket *.locallogic.co, because
          // that wildcard would also match non-API subdomains — sdk./staging.
          // (which serve the SDK JS bundle + static assets) and try./marketing
          // pages — turning a CDN/asset hiccup into a phantom API error.
          failedRequestTargets: [
            // Data + service APIs: api (v3 + neighborhood-management),
            // reports-api (leads reports), providers-api (commute/directions).
            // The //-prefix anchors to the host so patterns don't bleed.
            /\/\/(api|reports-api|providers-api)(-qa)?\.locallogic\.co/,
            // Analytics ingestion. These are fire-and-forget tracking calls, so
            // a failure won't break a widget — but now that we only report 5xx,
            // a 5xx here is a genuine signal our tracking backend is down, which
            // is worth knowing. Drop this line if the tracking noise returns.
            /\/\/analytics\.locallogic\.co/,
          ],
        }),
        httpContextIntegration(),
        extraErrorDataIntegration(),
      ],
      beforeSend(event) {
        // Drop errors from automated traffic (declared crawlers, scrapers,
        // headless browsers). They generate 4xx/5xx API failures at volume but
        // aren't actionable. See isLikelyBot for scope and limitations.
        if (isLikelyBot()) {
          return null;
        }

        // Collapse all dynamic-import/chunk-load failures into a single issue
        // and downgrade them to warning (see isChunkLoadError).
        if (isChunkLoadError(event)) {
          event.level = "warning";
          event.fingerprint = ["chunk-load-failure"];
          return event;
        }

        // Tag (rather than drop) errors thrown from third-party map libraries,
        // so they stay visible but can be muted or triaged by tag in the Sentry
        // UI. See getMapVendor.
        const mapVendor = getMapVendor(event);
        if (mapVendor) {
          event.tags = { ...event.tags, source: "map-vendor", mapVendor };
        }

        // Resource/fetch load failures ("Load failed"): keep them at ERROR
        // severity — when this is a Safari privacy-blocked request the SDK/map
        // does not render for the end user (LL22-11667), which is a real broken
        // experience, not benign noise. We only group them into one issue
        // (rather than let them fragment) and preserve any mapVendor tag set
        // above so a blocked *tile* fetch is distinguishable. These were
        // previously dropped entirely, which is why the failure was invisible.
        if (isResourceLoadError(event)) {
          event.fingerprint = ["resource-load-failed"];
          event.tags = {
            ...event.tags,
            source: event.tags?.source ?? "resource-load-failed",
          };
          return event;
        }

        return event;
      },
      sendDefaultPii: true,
      // Default is 3, but we have a lot of nested objects
      normalizeDepth: 5,
    });

    setContext("LocalLogic", {
      referrer: document.referrer,
      sdks_app_version: FULL_APP_VERSION,
      sdks_app_path: window?.location?.pathname?.substring(
        // The ?? - 1 is to fix "Unsafe arithmetic operation on optional chaining. It can result in NaN."
        (window?.location?.pathname?.lastIndexOf("/") ?? -1) + 1,
      ),
    });
  }

  public captureException(error: Error) {
    this.log(error.message, "error");

    sentryCaptureException(error);
  }

  public addBreadCrumb(
    category: string,
    message: string,
    data?: object,
    level: SentrySeverityLevel = "info",
  ) {
    sentryAddBreadcrumb({
      category,
      message,
      data,
      level,
    });
  }

  public setTags(tags: SentryTag) {
    sentrySetTags(tags);
  }

  /**
   * Generate useful console logs for debugging
   */
  private log(message: string, type: "error" | "warn" | "info" = "info") {
    console[type](CONSOLE_LOG_IDENTIFER + message);
  }
}

const singletonErrorTracker = Object.freeze(new ErrorTracker());
export default singletonErrorTracker;
